import React, { useMemo, useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import DragHandle from '../DragHandle';
import Container from '../Container';

const validateBounds = (style, current) => {
  let { left, top, width, height } = style;
  if (width < 0) {
    if (left !== current.left) {
      left += width;
    }
    width = 0;
  }
  if (height < 0) {
    if (top !== current.top) {
      top += height;
    }
    height = 0;
  }
  return {
    left,
    top,
    width,
    height,
  };
};

const resize = {
  topLeft: ({ current, stepX, stepY, aspectRatio }) => {
    const width = current.width - stepX;
    const height = aspectRatio
      ? width * (1 / aspectRatio)
      : current.height - stepY;
    return validateBounds(
      {
        left: current.left + stepX,
        top: aspectRatio
          ? current.top + current.height - height
          : current.top + stepY,
        width,
        height,
      },
      current
    );
  },
  topRight: ({ current, stepX, stepY, aspectRatio }) => {
    const width = current.width + stepX;
    const height = aspectRatio
      ? width * (1 / aspectRatio)
      : current.height - stepY;
    return validateBounds(
      {
        left: current.left,
        top: aspectRatio
          ? current.top + current.height - height
          : current.top + stepY,
        width,
        height,
      },
      current
    );
  },
  bottomRight: ({ current, stepX, stepY, aspectRatio }) => {
    const width = current.width + stepX;
    return validateBounds(
      {
        left: current.left,
        top: current.top,
        width,
        height: aspectRatio
          ? width * (1 / aspectRatio)
          : current.height + stepY,
      },
      current
    );
  },
  bottomLeft: ({ current, stepX, stepY, aspectRatio }) => {
    const width = current.width - stepX;
    return validateBounds(
      {
        left: current.left + stepX,
        top: current.top,
        width,
        height: aspectRatio
          ? width * (1 / aspectRatio)
          : current.height + stepY,
      },
      current
    );
  },
};

const actions = {
  move: ({ current, stepX, stepY }) => ({
    left: current.left + stepX,
    top: current.top + stepY,
  }),
  resize: ({ handle, ...args }) => resize[handle](args),
};

const getElementStyle = element =>
  ['top', 'left', 'width', 'height'].reduce(
    (aggregate, key) => ({
      ...aggregate,
      [key]: parseFloat(element.style[key]),
    }),
    {}
  );

const setElementStyle = (element, style) => {
  const e = element;
  ['top', 'left', 'width', 'height'].forEach(key => {
    e.style[key] = `${style[key]}px`;
  });
};

const Selector = React.forwardRef(
  ({ style: defaultStyle, onChanged, aspectRatio, ...props }, ref) => {
    const [style, setStyle] = useState(defaultStyle);

    useEffect(() => {
      setStyle(defaultStyle);
    }, [defaultStyle]);

    const onDrag = useCallback(
      (action, args) => {
        const { current: element } = ref;
        if (!element) {
          return;
        }
        const current = getElementStyle(element);
        const newStyle = {
          ...current,
          ...actions[action]({
            current,
            ...args,
          }),
        };
        setElementStyle(element, newStyle);

        if (args.dragState === 'stopped') {
          setStyle(newStyle);
        }

        onChanged({
          dragArgs: args,
          style: newStyle,
        });
      },
      [onChanged, ref]
    );

    const onDragBodyHandle = useCallback(
      args => onDrag('move', args),
      [onDrag]
    );

    const handles = useMemo(() => {
      const defaults = {
        position: 'absolute',
        width: '30px',
        height: '30px',
        border: 'solid 1px #2962ff',
        borderRadius: '15px',
        backgroundColor: '#fff',
        boxShadow: '0 0 6px 0 rgba(0, 0, 0, 0.5)',
      };
      const onHandleDrag = handle => args =>
        onDrag('resize', {
          handle,
          ...args,
          aspectRatio,
        });
      return [
        {
          top: '-15px',
          left: '-15px',
          onDrag: onHandleDrag('topLeft'),
          key: Math.random(),
        },
        {
          top: '-15px',
          right: '-15px',
          onDrag: onHandleDrag('topRight'),
          key: Math.random(),
        },
        {
          bottom: '-15px',
          right: '-15px',
          onDrag: onHandleDrag('bottomRight'),
          key: Math.random(),
        },
        {
          bottom: '-15px',
          left: '-15px',
          onDrag: onHandleDrag('bottomLeft'),
          key: Math.random(),
        },
      ].map(handle => ({
        ...defaults,
        ...handle,
      }));
    }, [aspectRatio, onDrag]);

    return (
      <Container ref={ref} position="absolute" style={style} {...props}>
        <DragHandle
          position="absolute"
          top="0px"
          left="0px"
          right="0px"
          bottom="0px"
          border="solid 1px #2962ff"
          boxShadow="0 0 6px 0 rgba(0, 0, 0, 0.5)"
          onDrag={onDragBodyHandle}
        />
        {handles.map(handle => (
          <DragHandle key={handle.key} {...handle} />
        ))}
      </Container>
    );
  }
);

Selector.propTypes = {
  style: PropTypes.shape({}),
  onChanged: PropTypes.func,
  aspectRatio: PropTypes.number,
};

Selector.defaultProps = {
  style: {},
  onChanged: () => {},
  aspectRatio: null,
};

export default Selector;
